import React, { useState, useEffect, useCallback, useRef } from 'react';
import TextField from '@material-ui/core/TextField';
import { CircularProgress, FormControl, InputAdornment, MenuItem, Select } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { usePhoneInput, defaultCountries, FlagImage, parseCountry } from 'react-international-phone';
import 'react-international-phone/style.css';
import { isValidPhoneNumber, isPossiblePhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import PhoneService from '../../services/PhoneService';
import useAdminUserSession from '../../../../hooks/useAdminUserSession';

const VALID = 'valid';
const POSSIBLE = 'possible';
const INVALID = 'invalid';
const ERROR = 'error';

const useStyles = makeStyles({
  borderValid: {
    '& .MuiOutlinedInput-root fieldset': { borderColor: '#16a34a !important' },
    '& .MuiOutlinedInput-root:hover fieldset': { borderColor: '#16a34a !important' },
    '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#16a34a !important' },
  },
  borderPossible: {
    '& .MuiOutlinedInput-root fieldset': { borderColor: '#d97706 !important' },
    '& .MuiOutlinedInput-root:hover fieldset': { borderColor: '#d97706 !important' },
    '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#d97706 !important' },
  },
  countrySelect: {
    marginRight: 4,
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      paddingTop: 4,
      paddingBottom: 4,
      paddingRight: '20px !important',
    },
    '& fieldset': { border: 'none' },
    '& .MuiSelect-iconOutlined': {
      right: 0
    },
  },
  countryMenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 200,
  },
  dialCode: {
    marginLeft: 'auto',
    paddingLeft: 12,
    color: '#666',
    fontSize: '0.85em',
  },
});

const buildRawString = (val, pre) => `${pre || ''}${val || ''}`.trim();

// Decides whether the stored (value, prefix) pair can be safely handed to the
// formatting hook. When false, we render the value verbatim in a plain TextField
// so the user sees the actual broken data instead of a mask-mangled version.
const isStoredPhoneSafe = (val, pre) => {
  if (!val) return true;
  // If value already contains a full international number, test it directly
  const withPrefix = (!pre && val.startsWith('+')) ? val : `${pre || '+1'} ${val}`.trim();
  try {
    const parsed = parsePhoneNumber(withPrefix);
    if (parsed?.isPossible()) return true;
  } catch {}
  // Fallback: try parsing the raw combined string with US context
  try {
    const parsed = parsePhoneNumber(buildRawString(val, pre), 'US');
    if (parsed?.isPossible()) return true;
  } catch {}
  return false;
};

const ContactInfoPhoneTextField = ({
  value = '',
  prefix = '',
  name,
  label,
  onChange,
  otherPhones = [],
  afterOnBlur,
  disabled = false,
  variant = 'outlined',
  onValidationStart,
  onValidationEnd,
  onFieldError,
  defaultCountry = 'us',
  ...textFieldProps
}) => {
  const { session } = useAdminUserSession();
  const classes = useStyles();

  const [validationState, setValidationState] = useState(null);
  const [validationMessage, setValidationMessage] = useState('');
  const [loadingValidation, setLoadingValidation] = useState(false);

  // Raw mode: stored value can't be parsed, so we display it as-is instead of
  // letting `usePhoneInput`'s country mask reformat it into a misleading shape.
  const [rawMode, setRawMode] = useState(() => !isStoredPhoneSafe(value, prefix));
  const [rawValue, setRawValue] = useState(() => buildRawString(value, prefix));

  const buildPhoneInputValue = useCallback((nextValue, nextPrefix) => {
    if (!nextValue && !nextPrefix) return '';
    if (!nextValue) return (nextPrefix || '');

    // If value already contains a full international number, don't prepend default +1
    const combined = (!nextPrefix && nextValue.startsWith('+'))
      ? nextValue
      : `${nextPrefix || '+1'} ${nextValue}`.trim();
    try {
      const parsed = parsePhoneNumber(combined);
      return parsed?.number || combined;
    } catch {
      return combined;
    }
  }, []);

  // Internal E164 state — avoids the controlled-value loop between this component and its parent.
  // We own the phone value internally; parent props are only used to initialize and to receive
  // updates pushed by AbstractAPI (external changes we detect via isInternalChange ref).
  const [internalPhone, setInternalPhone] = useState(() => {
    return buildPhoneInputValue(value, prefix);
  });

  const isInternalChange = useRef(false);
  // When no phone is stored, forceDialCode causes the library to fire onChange on mount
  // to inject the dial code prefix. Suppress this so we don't mark the form dirty.
  const suppressNextHookChange = useRef(!value && !prefix);
  // Tracks whether the user has actually edited the field. Used to skip the
  // blur-time AbstractAPI call + onChange that would otherwise silently
  // overwrite a stored invalid number with the mask-mangled version.
  const isDirty = useRef(false);
  const rawModeRef = useRef(rawMode);
  useEffect(() => { rawModeRef.current = rawMode; }, [rawMode]);

  const runInlineValidation = useCallback((e164, dialCode) => {
    if (!e164 || e164 === dialCode) {
      setValidationState(null);
      setValidationMessage('');
      return;
    }
    try {
      if (isValidPhoneNumber(e164)) {
        const parsed = parsePhoneNumber(e164);
        const type = parsed.getType();
        const typeLabel = { MOBILE: 'mobile', FIXED_LINE: 'landline', FIXED_LINE_OR_MOBILE: 'landline or mobile' }[type] || '';
        setValidationState(VALID);
        setValidationMessage(`Valid ${parsed.country || ''} ${typeLabel}`.trim());
      } else if (isPossiblePhoneNumber(e164)) {
        setValidationState(POSSIBLE);
        setValidationMessage('Looks valid but length is unusual');
      } else {
        setValidationState(INVALID);
        setValidationMessage('Invalid phone number');
      }
    } catch {
      setValidationState(INVALID);
      setValidationMessage('Invalid phone number');
    }
  }, []);

  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    // External update (initial mount or parent overwrote value): sync internal state
    // and re-evaluate whether the stored value is safe to format.
    const updated = buildPhoneInputValue(value, prefix);
    setInternalPhone(prev => {
      if (prev !== updated) {
        suppressNextHookChange.current = true;
        return updated;
      }
      return prev;
    });

    const safe = isStoredPhoneSafe(value, prefix);
    setRawMode(!safe);
    setRawValue(buildRawString(value, prefix));

    if (!safe && (value || prefix)) {
      setValidationState(INVALID);
      setValidationMessage('Invalid phone number');
    } else if (!value && !prefix) {
      setValidationState(null);
      setValidationMessage('');
    }
  }, [value, prefix, buildPhoneInputValue]);

  const normalizeDigits = (s) => (s || '').replace(/\D/g, '');

  const validateUniqueness = (localPart, dialCode) => {
    if (!localPart) return true;
    const norm = normalizeDigits(localPart);
    return !otherPhones.some(({ phone: op, prefix: pr }) =>
      normalizeDigits(op) === norm && (pr || '+1') === dialCode
    );
  };

  const handlePhoneChange = useCallback(({ phone: e164, country: c }) => {
    // While in raw mode the hook's emitted value is the mangled mask version —
    // ignore it so it can't leak into onChange and overwrite the stored data.
    if (rawModeRef.current) return;
    if (suppressNextHookChange.current) {
      suppressNextHookChange.current = false;
      return;
    }

    isDirty.current = true;
    isInternalChange.current = true;
    setInternalPhone(e164);

    const dialCode = '+' + c.dialCode;
    const sliced = e164.startsWith(dialCode) ? e164.slice(dialCode.length).trim() : '';
    let localPart = sliced;
    try {
      const parsed = parsePhoneNumber(e164);
      if (parsed) localPart = parsed.formatNational();
    } catch { /* fall back to sliced digits */ }

    runInlineValidation(e164, dialCode);

    if (localPart && !validateUniqueness(localPart, dialCode)) {
      setValidationState(ERROR);
      setValidationMessage('This phone number already exists');
    }

    if (onChange) {
      onChange({ phone: localPart, prefix: dialCode });
    }
  }, [onChange, otherPhones, runInlineValidation]);

  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
    defaultCountry: (defaultCountry || 'us').toLowerCase(),
    value: internalPhone,
    onChange: handlePhoneChange,
    forceDialCode: true,
  });

  const handleRawChange = (e) => {
    const next = e.target.value;
    isDirty.current = true;
    setRawValue(next);

    if (!next) {
      // Cleared — return to formatted (empty) mode
      setRawMode(false);
      setValidationState(null);
      setValidationMessage('');
      isInternalChange.current = true;
      suppressNextHookChange.current = true;
      setInternalPhone('');
      if (onChange) onChange({ phone: '', prefix: '+' + country.dialCode });
      return;
    }

    try {
      const parsed = parsePhoneNumber(next) || parsePhoneNumber(next, country.iso2.toUpperCase());
      if (parsed && parsed.isPossible()) {
        // Value is now parseable — hand off to the formatted input.
        const dialCode = '+' + parsed.countryCallingCode;
        const localPart = parsed.formatNational();
        const iso2 = parsed.country?.toLowerCase();

        isInternalChange.current = true;
        suppressNextHookChange.current = true;
        setRawMode(false);
        setInternalPhone(parsed.number);
        if (iso2 && iso2 !== country.iso2) setCountry(iso2);
        runInlineValidation(parsed.number, dialCode);
        if (onChange) {
          onChange({ phone: localPart, prefix: dialCode });
        }
        return;
      }
    } catch { /* stay in raw mode */ }

    setValidationState(INVALID);
    setValidationMessage('Invalid phone number');

    if (onChange) {
      const prefixMatch = next.match(/^(\+\d{1,4})(.*)$/);
      if (prefixMatch) {
        isInternalChange.current = true;
        onChange({ phone: prefixMatch[2].trim(), prefix: prefixMatch[1] });
      } else {
        isInternalChange.current = true;
        onChange({ phone: next, prefix: '' });
      }
    }
  };

  // Intercept pastes so libphonenumber-js can normalise before the hook sees the raw text.
  //
  // Two cases we handle:
  //   1. International format (+44 020 3955 9910, +1 (656) 653-5550): the raw paste would land
  //      as "+44 +44 …" because forceDialCode already prefixes the field — duplicate dial code.
  //   2. Local format with trunk prefix (020 3955 9910 for GB): the library would keep the
  //      leading 0, producing "+44 0203 955991" — one digit too many, flagged as unusual length.
  //      libphonenumber-js knows to drop trunk prefixes when given a country context.
  const handlePaste = (event) => {
    isDirty.current = true;
    // In raw mode, let the default paste flow through so handleRawChange runs.
    if (rawMode) return;

    const pasted = event.clipboardData.getData('text').trim();
    if (!pasted) return;

    let parsed = null;
    try {
      parsed = pasted.startsWith('+')
        ? parsePhoneNumber(pasted)
        : parsePhoneNumber(pasted, country.iso2.toUpperCase());
    } catch { /* parsed stays null */ }

    // If the paste parses to a possible phone, hand off to the formatted input.
    // Otherwise switch to raw mode so the user sees what they actually pasted —
    // the mask would otherwise truncate the digits and hide the real value.
    if (parsed && parsed.isPossible()) {
      event.preventDefault();
      const iso2 = parsed.country?.toLowerCase();
      const e164 = parsed.number;
      const dialCode = '+' + parsed.countryCallingCode;
      const localPart = parsed.formatNational();

      if (iso2 && iso2 !== country.iso2) setCountry(iso2);
      suppressNextHookChange.current = true;
      isInternalChange.current = true;
      setInternalPhone(e164);
      runInlineValidation(e164, dialCode);
      if (onChange) {
        onChange({ phone: localPart, prefix: dialCode });
      }
      return;
    }

    event.preventDefault();
    setRawMode(true);
    setRawValue(pasted);
    setValidationState(INVALID);
    setValidationMessage('Invalid phone number');

    if (onChange) {
      isInternalChange.current = true;
      const prefixMatch = pasted.match(/^(\+\d{1,4})(.*)$/);
      if (prefixMatch) {
        onChange({ phone: prefixMatch[2].trim(), prefix: prefixMatch[1] });
      } else {
        onChange({ phone: pasted, prefix: '' });
      }
    }
  };

  const handleBlur = async (event) => {
    // Skip server validation + onChange overwrite when the user never edited the field.
    // Without this, blurring an unmodified invalid number triggers AbstractAPI with the
    // mangled mask value and silently replaces the stored data with the truncated version.
    if (!isDirty.current) {
      if (afterOnBlur) afterOnBlur(event);
      return;
    }

    if (rawMode) {
      setValidationState(INVALID);
      setValidationMessage('Invalid phone number');
      if (afterOnBlur) afterOnBlur(event);
      return;
    }

    const currentInput = event.target.value?.trim();
    const dialCode = '+' + country.dialCode;

    if (!currentInput || currentInput === dialCode) {
      if (afterOnBlur) afterOnBlur(event);
      return;
    }

    const localPart = internalPhone.startsWith(dialCode)
      ? internalPhone.slice(dialCode.length).trim()
      : '';

    if (!validateUniqueness(localPart, dialCode)) {
      setValidationState(ERROR);
      setValidationMessage('Phones must differ');
      if (afterOnBlur) afterOnBlur(event);
      return;
    }

    if (onValidationStart) onValidationStart();
    setLoadingValidation(true);

    try {
      const response = await PhoneService.validate(session, `${dialCode} ${localPart}`.trim());
      const { status, message, phone: validated } = response.data;

      if (status === 'success') {
        setValidationState(VALID);
        setValidationMessage('');

        let storedPhone = validated.local_format;
        let storedPrefix = validated.country_prefix;
        try {
          const normalized = parsePhoneNumber(validated.local_format, country.iso2.toUpperCase());
          if (normalized) {
            storedPhone = normalized.formatNational();
            storedPrefix = '+' + normalized.countryCallingCode;
          }
        } catch { /* fall back to raw AbstractAPI values */ }

        if (onChange) onChange({ phone: storedPhone, prefix: storedPrefix });
      } else {
        setValidationState(ERROR);
        setValidationMessage(message);
      }
    } catch (err) {
      setValidationState(ERROR);
      setValidationMessage('Validation error: ' + err.message);
    } finally {
      setLoadingValidation(false);
      if (onValidationEnd) onValidationEnd();
      if (afterOnBlur) afterOnBlur(event);
    }
  };

  // Report validation errors to parent so the Save button can be disabled
  useEffect(() => {
    if (onFieldError) {
      const hasError = validationState === INVALID || validationState === ERROR || validationState == POSSIBLE; // If possible, we want first to validate it until we wllow to edit
      onFieldError(name, hasError);
    }
  }, [validationState]);

  // Clear error on unmount so stale errors don't block Save
  useEffect(() => {
    return () => {
      if (onFieldError) onFieldError(name, false);
    };
  }, []);

  const borderClass = { [VALID]: classes.borderValid, [POSSIBLE]: classes.borderPossible }[validationState] || '';
  const isError = validationState === INVALID || validationState === ERROR;
  const msgColor = { [VALID]: '#16a34a', [POSSIBLE]: '#d97706', [INVALID]: '#dc2626', [ERROR]: '#dc2626' }[validationState];

  return (
    <FormControl fullWidth className={borderClass}>
      <TextField
        {...textFieldProps}
        name={name}
        label={label}
        value={rawMode ? rawValue : inputValue}
        onChange={rawMode ? handleRawChange : handlePhoneValueChange}
        onBlur={handleBlur}
        onPaste={handlePaste}
        variant={variant}
        disabled={disabled}
        error={isError}
        inputRef={rawMode ? undefined : inputRef}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" style={{ marginRight: 0 }}>
              <Select
                value={country.iso2}
                onChange={(e) => setCountry(e.target.value)}
                disabled={disabled}
                className={classes.countrySelect}
                variant="outlined"
                renderValue={(iso2) => <FlagImage iso2={iso2} style={{ display: 'block', width: 24, height: 'auto' }} />}
                MenuProps={{ style: { zIndex: 9999 } }}
              >
                {defaultCountries.map((c) => {
                  const p = parseCountry(c);
                  return (
                    <MenuItem key={p.iso2} value={p.iso2} className={classes.countryMenuItem}>
                      <FlagImage iso2={p.iso2} style={{ width: 20, height: 'auto', flexShrink: 0 }} />
                      <span>{p.name}</span>
                      <span className={classes.dialCode}>+{p.dialCode}</span>
                    </MenuItem>
                  );
                })}
              </Select>
            </InputAdornment>
          ),
          endAdornment: loadingValidation ? (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ) : null,
        }}
        fullWidth
      />
      {validationMessage && (
        <span style={{ color: msgColor, fontSize: '12px', marginTop: 4 }}>
          {validationMessage}
        </span>
      )}
    </FormControl>
  );
};

export default ContactInfoPhoneTextField;
